/**
 * FreshCards Subsystem
 * Phase 3: Shell Layer - Main Subsystem Class
 * 
 * Core orchestrator class that manages the FreshCards application lifecycle,
 * coordinates between system modules, and provides an embeddable interface
 * for host applications.
 */

import { EventBus } from '../core/events/EventBus';
import { ModuleLoader } from '../core/modules/ModuleLoader';
import { SurfaceRegistry } from '../core/surfaceRegistry';
import { commandRegistry } from '../core/command-registry';
import { registerCommandHandlers } from '../core/command-handlers';
import { createPage, initializeStorageSystem } from '../core/storage';
import { initGlobalSurfaceRegistry } from '../core/surfaceRegistry';
import type { SurfaceDefinition } from '../core/surfaces/types';
import { getRuntimeManifest } from '../runtime/analysis';

// Import surface components directly
import HomePage from '../components/surfaces/HomePage';
import PageEditor from '../components/surfaces/PageEditor';
import PageTree from '../components/surfaces/PageTree';
import BlockPalette from '../components/surfaces/BlockPalette';
import BlockPreview from '../components/surfaces/BlockPreview';
import AIChat from '../components/surfaces/AIChat';
import DatabaseView from '../components/surfaces/DatabaseView';
import QuickNote from '../components/surfaces/QuickNote';

export interface FreshCardsConfig {
  initialSurface?: string;
  standalone?: boolean;
  hostCallbacks?: {
    onNavigate?: (surface: string, params?: any) => void;
    onSurfaceChange?: (surface: string, params?: any) => void;
    onCommandExecute?: (command: string, params?: any) => void;
  };
  customConfig?: Record<string, any>;
}

export interface FreshCardsState {
  activeSurface: string;
  rightPanel: {
    open: boolean;
    surface: string | null;
    collapsed: boolean;
  };
  commandPalette: {
    open: boolean;
    search: string;
  };
  currentPageId: string | undefined;
  status: 'initializing' | 'ready' | 'error' | 'loading';
  error?: string;
}

export interface FreshCardsSubsystemProps {
  config?: FreshCardsConfig | undefined;
  onStateChange?: (state: FreshCardsState) => void;
  onError?: (error: Error) => void;
}

export class FreshCardsSubsystem {
  private config: FreshCardsConfig;
  private state: FreshCardsState;
  private eventBus: EventBus;
  private moduleLoader: ModuleLoader;
  private surfaceRegistry: SurfaceRegistry;
  private initialized = false;
  private destroyed = false;

  constructor(props: FreshCardsSubsystemProps = {}) {
    this.config = {
      initialSurface: 'HomePage',
      standalone: true,
      hostCallbacks: {},
      customConfig: {},
      ...props.config
    };

    this.state = {
      activeSurface: this.config.initialSurface!,
      rightPanel: {
        open: false,
        surface: null,
        collapsed: false
      },
      commandPalette: {
        open: false,
        search: ''
      },
      currentPageId: undefined,
      status: 'initializing'
    };

    this.eventBus = new EventBus();
    this.moduleLoader = new ModuleLoader(this.eventBus, null as any);
    this.surfaceRegistry = initGlobalSurfaceRegistry(this.moduleLoader, this.eventBus);
    this.moduleLoader.setSurfaceRegistry(this.surfaceRegistry);

    if (props.onStateChange) {
      this.subscribeToStateChanges(props.onStateChange);
    }

    if (props.onError) {
      this.eventBus.on('error', (error) => {
        props.onError!(error instanceof Error ? error : new Error(String(error)));
      });
    }
  }

  async initialize(): Promise<void> {
    if (this.initialized || this.destroyed) {
      throw new Error('Subsystem cannot be initialized');
    }

    try {
      this.updateState({ status: 'initializing' });

      const manifest = getRuntimeManifest();
      console.log('[RuntimeAnalysis] Platform:', manifest.metadata.platform, '| Version:', manifest.version, '| Build:', manifest.build);

      await initializeStorageSystem();
      console.log('Storage system initialized');

      registerCommandHandlers();

      await this.moduleLoader.loadModulesFromDirectory('./modules');
      console.log('Modules loaded');

      await this.registerCoreSurfaces();

      this.initialized = true;
      this.updateState({ status: 'ready' });

      this.eventBus.emit('subsystem-initialized', {
        timestamp: new Date(),
        config: this.config,
        runtime: getRuntimeManifest()
      });

    } catch (error) {
      console.error('Failed to initialize subsystem:', error);
      this.updateState({
        status: 'error',
        error: error instanceof Error ? error.message : String(error)
      });
      throw error;
    }
  }

  private async registerCoreSurfaces(): Promise<void> {
    const surfaces = [
      { name: 'HomePage', component: HomePage, type: 'workspace' as const },
      { name: 'PageEditor', component: PageEditor, type: 'workspace' as const },
      { name: 'PageTree', component: PageTree, type: 'workspace' as const },
      { name: 'BlockPalette', component: BlockPalette, type: 'workspace' as const },
      { name: 'BlockPreview', component: BlockPreview, type: 'peek' as const },
      { name: 'AIChat', component: AIChat, type: 'peek' as const },
      { name: 'DatabaseView', component: DatabaseView, type: 'workspace' as const },
      { name: 'QuickNote', component: QuickNote, type: 'peek' as const }
    ];

    for (const surface of surfaces) {
      try {
        this.surfaceRegistry.registerSurface(
          surface.name,
          surface.component,
          'FreshCards',
          surface.type
        );
        console.log('Registered surface:', surface.name);
      } catch (error) {
        console.error('Failed to register surface', surface.name, error);
      }
    }
  }

  async navigateTo(surface: string, params?: any): Promise<void> {
    if (!this.initialized || this.destroyed) {
      throw new Error('Subsystem not initialized');
    }

    try {
      this.updateState({
        activeSurface: surface,
        currentPageId: surface === 'PageEditor' ? params?.pageId : undefined
      });

      const surfaceDef = this.surfaceRegistry.getSurface(surface);
      if (surfaceDef && surfaceDef.type === 'peek') {
        this.updateState({
          rightPanel: { open: true, surface, collapsed: false }
        });
      } else {
        this.updateState({
          rightPanel: { open: false, surface: null, collapsed: false }
        });
      }

      if (surface) {
        const url = new URL(window.location.href);
        url.searchParams.set('surface', surface);
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            url.searchParams.set(key, String(value));
          });
        }
        window.history.pushState({}, '', url);
      }

      if (surfaceDef) {
        (surfaceDef as any).props = params || {};
      }

      this.eventBus.emit('navigated', {
        surface,
        params,
        timestamp: new Date()
      });

      if (this.config.hostCallbacks?.onNavigate) {
        this.config.hostCallbacks.onNavigate(surface, params);
      }

      window.scrollTo(0, 0);

    } catch (error) {
      console.error('Navigation failed:', error);
      throw error;
    }
  }

  async createPage(title: string = 'New Page'): Promise<any> {
    try {
      const newPage = await createPage(title);
      console.log('Created new page:', newPage);

      this.eventBus.emit('page-created', {
        pageId: newPage.id,
        type: 'page-created',
        timestamp: new Date()
      });

      await this.navigateTo('PageEditor', { pageId: newPage.id });

      return newPage;
    } catch (error) {
      console.error('Failed to create page:', error);
      throw error;
    }
  }

  async executeCommand(commandName: string, params?: any): Promise<any> {
    try {
      const result = await commandRegistry.executeCommand(commandName);

      if (this.config.hostCallbacks?.onCommandExecute) {
        this.config.hostCallbacks.onCommandExecute(commandName, params);
      }

      return result;
    } catch (error) {
      console.error('Command execution failed:', error);
      throw error;
    }
  }

  openCommandPalette(): void {
    this.updateState({ commandPalette: { open: true, search: '' } });
  }

  closeCommandPalette(): void {
    this.updateState({ commandPalette: { open: false, search: '' } });
  }

  updateCommandSearch(search: string): void {
    this.updateState({ commandPalette: { ...this.state.commandPalette, search } });
  }

  getCurrentSurface(): SurfaceDefinition | null {
    return this.surfaceRegistry.getSurface(this.state.activeSurface) || null;
  }

  getAllSurfaces(): SurfaceDefinition[] {
    return this.surfaceRegistry.getAllSurfaces();
  }

  getAllCommands(): any[] {
    return commandRegistry.getAllCommands();
  }

  getActionButtons(): any[] {
    const surface = this.getCurrentSurface();
    if (!surface) return [];

    switch (surface.name) {
      case 'HomePage':
        return [{ id: 'create-page', label: 'Create Page', onClick: () => this.createPage(), phase: 1 }];
      case 'PageEditor':
        return [{ id: 'save-page', label: 'Save Page', onClick: () => { this.eventBus.emit('page-save-requested', { pageId: this.state.currentPageId, timestamp: new Date() }); }, phase: 1 }];
      default:
        return [];
    }
  }

  getCurrentPageId(): string | undefined {
    return this.state.currentPageId;
  }

  getState(): FreshCardsState {
    return { ...this.state };
  }

  private updateState(newState: Partial<FreshCardsState>): void {
    this.state = { ...this.state, ...newState };
    this.eventBus.emit('state-changed', { state: this.state, timestamp: new Date() });
  }

  private subscribeToStateChanges(callback: (state: FreshCardsState) => void): () => void {
    const handler = (data: any) => callback(data.state);
    this.eventBus.on('state-changed', handler);
    return () => { this.eventBus.off('state-changed', handler); };
  }

  async destroy(): Promise<void> {
    if (this.destroyed) return;

    try {
      await this.moduleLoader.unloadModule('main');
      this.surfaceRegistry.getAllSurfaces().forEach(surface => {
        this.surfaceRegistry.unregisterSurface(surface.name);
      });
      this.destroyed = true;
      this.updateState({ status: 'error' });
      console.log('FreshCards subsystem destroyed');
    } catch (error) {
      console.error('Error during subsystem destruction:', error);
      throw error;
    }
  }

  isInitialized(): boolean { return this.initialized && !this.destroyed; }
  isDestroyed(): boolean { return this.destroyed; }
  getConfig(): FreshCardsConfig { return { ...this.config }; }
  updateConfig(newConfig: Partial<FreshCardsConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.eventBus.emit('config-changed', { config: this.config, timestamp: new Date() });
  }
  getEventBus(): EventBus { return this.eventBus; }
  getModuleLoader(): ModuleLoader { return this.moduleLoader; }
  getSurfaceRegistry(): SurfaceRegistry { return this.surfaceRegistry; }
}

export default FreshCardsSubsystem;
